import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Landmark, Wallet, Save } from "lucide-react";

const L = "text-[10px] uppercase tracking-[0.2em] font-semibold";

/** Coordonnées de versement du BP (IBAN chiffré côté serveur, affiché masqué) ou email PayPal. */
export const BpPayoutMethod = ({ onSaved }) => {
  const [cur, setCur] = useState(null);
  const [form, setForm] = useState({ method: "iban", holder: "", iban: "", bic: "", paypal_email: "" });
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/bp/payout-method").then((r) => { setCur(r.data); setForm((f) => ({ ...f, method: r.data.method || "iban", holder: r.data.holder || "", bic: r.data.bic || "", paypal_email: r.data.paypal_email || "" })); }).catch(() => setCur({}));
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/bp/payout-method", form); setCur(data); setForm((f) => ({ ...f, iban: "" })); toast.success("Coordonnées de versement enregistrées"); onSaved?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-[#DEE2E6] p-5 space-y-4" id="versement" data-testid="bp-payout-method">
      {cur?.method && (
        <div className="text-xs border border-[#A7F3D0] bg-[#ECFDF5] text-[#065F46] p-2" data-testid="bp-payout-current">
          Versements actuels par <b>{cur.method === "paypal" ? `PayPal (${cur.paypal_email})` : `virement — ${cur.iban_masked}${cur.bic ? ` · ${cur.bic}` : ""}`}</b>{cur.holder ? ` · ${cur.holder}` : ""}.
        </div>
      )}
      {!cur?.method && cur && <div className="text-xs border-2 border-[#FFD700] bg-[#FFFBEB] p-2" data-testid="bp-payout-missing">Renseignez vos coordonnées pour recevoir vos commissions (versement le 10 du mois).</div>}
      <div className="grid grid-cols-2 gap-2">
        {[["iban", "Virement bancaire (IBAN)", Landmark], ["paypal", "PayPal", Wallet]].map(([k, l, Icon]) => (
          <button key={k} type="button" onClick={() => setForm((f) => ({ ...f, method: k }))} data-testid={`bp-payout-method-${k}`}
                  className={`p-3 border-2 text-left text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 ${form.method === k ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#DEE2E6] hover:border-[#0A0A0A]"}`}><Icon className="h-4 w-4" />{l}</button>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label className={L}>Titulaire du compte {form.method === "iban" ? "*" : ""}</Label><Input value={form.holder} onChange={set("holder")} data-testid="bp-payout-holder" /></div>
        {form.method === "iban" ? (
          <>
            <div className="space-y-1.5"><Label className={L}>IBAN *</Label><Input value={form.iban} onChange={set("iban")} placeholder={cur?.iban_masked || "FR76 •••• •••• •••• ••••"} className="font-mono" data-testid="bp-payout-iban" /></div>
            <div className="space-y-1.5"><Label className={L}>BIC</Label><Input value={form.bic} onChange={set("bic")} className="font-mono uppercase" maxLength={11} data-testid="bp-payout-bic" /></div>
          </>
        ) : (
          <div className="space-y-1.5"><Label className={L}>Email du compte PayPal *</Label><Input type="email" value={form.paypal_email} onChange={set("paypal_email")} data-testid="bp-payout-paypal" /></div>
        )}
      </div>
      <Button onClick={save} disabled={saving} className="bg-[#002FA7] text-white" data-testid="bp-payout-save"><Save className="h-4 w-4 mr-2" />{saving ? "Enregistrement…" : "Enregistrer mes coordonnées"}</Button>
      <p className="text-[10px] text-[#868E96]">L'IBAN est chiffré et n'est jamais réaffiché en clair ; seule l'équipe PAG y accède au moment du virement.</p>
    </div>
  );
};
